<template>
  <Space direction="vertical" style="width: 100%">
    <div class="info-container" :class="bannerClass">
      <div class="icon">
        <LoadingOutlined
          v-if="payPhase === 'pending'"
          spin
          style="font-size: 48px; color: #2563eb"
        />
        <IconFont
          v-else-if="payPhase === 'success'"
          type="icon-wancheng"
          style="font-size: 50px"
        />
        <CloseCircleFilled
          v-else
          style="font-size: 48px; color: #dc2626"
        />
      </div>
      <div class="info">
        <div class="top">
          <span class="headline"
            >{{ headerTicketStatus }}</span
          >
          <span class="thank-text">感谢您选择铁路出行！您的订单号为：</span>
          <span class="order-text">{{ state?.orderDetail?.orderSn }}</span>
        </div>
        <div v-if="payPhase === 'pending'" class="tip-text">
          正在为您出票，请稍候…
        </div>
        <div v-else-if="payPhase === 'success'" class="tip-text">
          {{ passengerNameHint }}{{ passengerHonorific }}可持购票时所使用的有效身份证件原件，于列车开车前到车站检票乘车（电子客票无需换取纸质车票）。
        </div>
        <div v-else class="tip-text fail-tip">
          <template v-if="ticketFailReason">
            出票失败，失败原因：{{ ticketFailReason }}
          </template>
          <template v-else>
            出票失败，请前往订单列表查看详情或联系客服处理。
          </template>
        </div>
      </div>
    </div>
    <div class="custom">
      <Card title="订单信息">
        <div class="ticket-info">
          <span class="main-text"
            >{{ state.orderDetail?.ridingDate }}（{{
              getWeekNumber(dayjs(state.orderDetail?.ridingDate).day())
            }}）{{ state.orderDetail?.trainNumber }}</span
          >
          <span class="small-text">次</span>
          <span class="main-text">{{ state.orderDetail?.departure }}</span>
          <span class="small-text">站</span>
          <span class="main-text"
            >（{{ state.orderDetail?.departureTime }}开）-{{
              state.orderDetail?.arrival
            }}</span
          >
          <span class="small-text"
            >站（{{ state.orderDetail?.arrivalTime }}到）</span
          >
        </div>
        <Table
          :key="`order-table-${ticketFailReason}`"
          :columns="column"
          size="small"
          :data-source="state.orderDetail?.passengerDetails"
          :pagination="false"
        ></Table>
        <Divider></Divider>
        <Space style="justify-content: center; width: 100%">
          <Button :disabled="true">餐饮·特产</Button>
          <Button @click="router.push('/ticketSearch')">继续购票</Button>
          <Button @click="goOrderList">查看订单详情</Button>
        </Space>
      </Card>
    </div>
  </Space>
</template>

<script setup scoped>
import { Card, Space, Table, Divider, Button } from 'ant-design-vue'
import IconFont from '@/components/icon-font'
import { LoadingOutlined, CloseCircleFilled } from '@ant-design/icons-vue'
import { fetchOrderBySn, fetchOrderStatusLog } from '@/service'
import { computed, h, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import {
  ID_CARD_TYPE,
  TICKET_TYPE_LIST,
  SEAT_CLASS_TYPE_LIST,
  TICKET_STATUS_LIST
} from '@/constants'
import { getWeekNumber } from '@/utils'
import { parseTicketFailReason } from '@/utils/orderDisplay'

const ticketFailReason = ref('')

const renderOrderStatus = (status) => {
  const label =
    TICKET_STATUS_LIST.find((item) => item.value === status)?.label ?? '—'
  if (status === 5 && ticketFailReason.value) {
    return h('div', { class: 'status-fail-cell' }, [
      h('div', label),
      h('div', { class: 'status-fail-reason' }, `失败原因：${ticketFailReason.value}`)
    ])
  }
  return h('div', label)
}

const column = computed(() => [
  {
    title: '序号',
    dataIndex: 'id',
    customRender: ({ text, record, index }) =>
      h('div', { innerHTML: index + 1 })
  },
  {
    title: '姓名',
    dataIndex: 'realName'
  },
  {
    title: '证件类型',
    dataIndex: 'idType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: ID_CARD_TYPE.find((item) => item.value === text)?.label
      })
  },
  {
    title: '证件号码',
    dataIndex: 'idCard'
  },
  {
    title: '乘客类型',
    dataIndex: 'ticketType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: TICKET_TYPE_LIST.find((item) => item.value === text)?.label
      })
  },
  {
    title: '席别',
    dataIndex: 'seatType',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: SEAT_CLASS_TYPE_LIST.find((item) => item.code === text)
          ?.label
      })
  },
  {
    title: '车厢',
    dataIndex: 'carriageNumber',
    customRender: ({ text }) =>
      h('div', {
        innerHTML: text + '车'
      })
  },
  {
    title: '席位号',
    dataIndex: 'seatNumber'
  },
  {
    title: '票价（元）',
    dataIndex: 'amount',
    customRender: ({ text }) =>
      h('a', {
        innerHTML: text / 100 + '¥'
      })
  },
  {
    title: '订单状态',
    dataIndex: 'status',
    customRender: ({ text }) => renderOrderStatus(text)
  }
])

const { query } = useRoute()
const router = useRouter()
const state = reactive({
  orderDetail: null
})
let timer = null

/** 顶部购票结果文案：与表格乘客/订单状态一致 */
const headerTicketStatus = computed(() => {
  const order = state.orderDetail
  if (!order) return '出票中'
  if (order.status === 5) return '出票失败'
  const passengers = order.passengerDetails ?? []
  if (passengers.some((p) => p.status === 5)) return '出票失败'
  if (order.status === 3 || passengers.every((p) => p.status === 3)) {
    return '购票成功'
  }
  return '出票中'
})

const payPhase = computed(() => {
  const status = headerTicketStatus.value
  if (status === '购票成功') return 'success'
  if (status === '出票失败') return 'failed'
  return 'pending'
})

const bannerClass = computed(() => ({
  'banner-pending': payPhase.value === 'pending',
  'banner-success': payPhase.value === 'success',
  'banner-failed': payPhase.value === 'failed'
}))

/** 顶部提示姓名与订单乘客表一致 */
const passengerNameHint = computed(() => {
  const passengers = state.orderDetail?.passengerDetails ?? []
  const names = passengers.map((p) => p.realName).filter(Boolean)
  return names.join('、')
})

const passengerHonorific = computed(() => {
  const count = state.orderDetail?.passengerDetails?.length ?? 0
  return count > 1 ? '等乘车人' : '先生/女士'
})

const goOrderList = () => {
  const orderSn = state.orderDetail?.orderSn || query?.orderSn
  router.push({
    path: '/ticketList',
    query: orderSn ? { tab: '1', orderSn: String(orderSn) } : { tab: '1' }
  })
}

const shouldPollOrderStatus = () => {
  const order = state.orderDetail
  if (!order) return true
  if (order.status === 3 || order.status === 5) return false
  const passengers = order.passengerDetails ?? []
  if (passengers.every((p) => p.status === 3)) return false
  if (passengers.some((p) => p.status === 5)) return false
  return true
}

const loadTicketFailReason = (orderSn) => {
  if (!orderSn) return
  fetchOrderStatusLog({ orderSn })
    .then((res) => {
      if (res?.success && Array.isArray(res.data)) {
        ticketFailReason.value = parseTicketFailReason(res.data)
      }
    })
    .catch(() => {
      ticketFailReason.value = ''
    })
}

const syncOrderDetail = (order) => {
  state.orderDetail = order
  const failed =
    order?.status === 5 ||
    order?.passengerDetails?.some((p) => p.status === 5)
  if (failed) {
    loadTicketFailReason(order?.orderSn || query?.orderSn)
  }
}

const startPollingOrderStatus = () => {
  if (timer) return
  timer = setInterval(() => {
    fetchOrderBySn({ orderSn: query?.orderSn }).then((res) => {
      if (res?.data) {
        syncOrderDetail(res.data)
      }
      if (!shouldPollOrderStatus()) {
        clearInterval(timer)
        timer = null
      }
    })
  }, 1000)
}

onMounted(() => {
  fetchOrderBySn({ orderSn: query?.orderSn }).then((res) => {
    if (res?.data) {
      syncOrderDetail(res.data)
    }
    if (shouldPollOrderStatus()) {
      startPollingOrderStatus()
    }
  })
})

// 组件卸载时清除定时器
onUnmounted(() => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
})
</script>

<style lang="scss" scoped>
.info-container {
  width: 100%;
  padding: 20px 40px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 26px rgba(37, 99, 235, 0.08);

  .icon {
    margin-right: 30px;
  }

  .info {
    .top {
      margin-bottom: 6px;

      .headline {
        font-size: 18px;
        font-weight: bolder;
        margin-right: 6px;
      }

      .thank-text {
        font-size: 14px;
        color: #0f172a;
      }

      .order-text {
        font-size: 16px;
        color: #ea580c;
        font-weight: bolder;
      }
    }

    .tip-text {
      color: rgba(0, 0, 0, 0.6);
      font-size: 14px;
    }

    .fail-tip {
      color: #b91c1c;
    }
  }
}

.banner-pending {
  border: 1px solid #bfdbfe;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);

  .headline {
    color: #1d4ed8;
  }
}

.banner-success {
  border: 1px solid #c9e6cc;
  background: linear-gradient(180deg, #f4fff0 0%, #ecfce8 100%);
  box-shadow: 0 10px 26px rgba(34, 197, 94, 0.12);

  .headline {
    color: #16a34a;
  }
}

.banner-failed {
  border: 1px solid #fecaca;
  background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);

  .headline {
    color: #dc2626;
  }
}
.ticket-info {
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid #e5edf8;
  border-radius: 10px;
  background: #f8fbff;
  .main-text {
    font-size: 16px;
    font-weight: bolder;
    color: #0f172a;
  }
  .small-text {
    font-size: 14px;
    color: #64748b;
  }
}
::v-deep {
  .ant-table-thead {
    .ant-table-cell {
      background: #f3f3f3 !important;
      background-color: #f3f3f3 !important;
    }
  }

  .ant-card {
    border-radius: 14px;
  }
}

.status-fail-cell {
  line-height: 1.5;
}

.status-fail-reason {
  margin-top: 4px;
  color: #dc2626;
  font-size: 12px;
}
</style>
